import { memo, type ReactNode, useMemo } from "react";
import { type SearchResultRow, type SrpProductData } from "../../../../../utils/models";
import { useListVisibilityWatcher } from "../../../../../common/hooks/useListVisibilityWatcher";
import { type RootState } from "../../../../../redux/store";
import { searchAfterScroll, searchItemClick } from "../../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { SearchResultProductNonCustomer } from "./SearchResultProductNonCustomer";
import { userIsCustomer } from "../../../../../utils/roles";
import { SearchResultProductCustomer } from "./SearchResultProductCustomer";
import uniq from "lodash/uniq";
import {
  getToolConfiguration,
  isAssistantConfig,
  isConfiguratorConfig,
  type LaunchToolConfig,
} from "../../../../../feature/LaunchRelevantTool/ToolConfig";
import { LaunchAssistantsSection, LaunchConfiguratorsSection } from "./components/LaunchToolsSection";
import { SearchPageType, useSearchPageType } from "~/Search/useSearchPageType";
import { SearchResultListItemCampaign } from "~/Search/components/ProductResults/views/ListView/SearchResultCampaign";

import { isNonNull } from "~/utils/reducers";
import { selectSearchQuery } from "~/Search/redux/searchSelectors";
import { SearchResultListItemNew } from "./components/SearchResultListItemNew/SearchResultListItemNew";
import { selectIsUseNewDesign } from "~/feature/UserPreferences/userPreferencesSelectors";

interface SearchResultListProps {
  results: SearchResultRow[];
  recommendationPosition: number | undefined;
  recommendationComponent: ReactNode | undefined;
}

type RenderRowFunc = (row: SearchResultRow, index: number) => ReactNode;

const SearchResultList = ({ results, recommendationPosition, recommendationComponent }: SearchResultListProps) => {
  const dispatch = useDispatch();
  const { onSearchItemClick, onAfterScroll } = useMemo(
    () => ({
      onSearchItemClick: (area: string, product: SrpProductData) => dispatch(searchItemClick(area, product)),
      onAfterScroll: (oldLastSeen: number, newLastSeen: number) =>
        dispatch(searchAfterScroll(oldLastSeen, newLastSeen)),
    }),
    [dispatch],
  );

  const listRef = useListVisibilityWatcher<HTMLUListElement>(onAfterScroll, ".jsSearchResultProduct", results);
  const searchQuery = useSelector(selectSearchQuery);

  const configuratorsToShow: LaunchToolConfig[] = useSelector((state: RootState) => {
    return uniq(results.flatMap((row) => state.products.basic[row.productCode]?.configuratorTypes))
      .filter(isNonNull)
      .sort()
      .map(getToolConfiguration);
  });

  const assistantTools = configuratorsToShow.filter(isAssistantConfig);
  const configuratorTools = configuratorsToShow.filter(isConfiguratorConfig);

  const pageType = useSearchPageType();
  const isSalesCampaignPage = pageType === SearchPageType.Campaign;

  let addQualtricsContainer = true;
  if (pageType === SearchPageType.Campaign) {
    addQualtricsContainer = false;
  }

  const useNewRender = useSelector(selectIsUseNewDesign);

  let renderRow: RenderRowFunc;
  if (isSalesCampaignPage) {
    renderRow = (row, index) => (
      <SearchResultListItemCampaign
        key={row.id}
        productCode={row.productCode}
        onProductClick={onSearchItemClick}
        index={index}
        searchQuery={searchQuery}
      />
    );
  } else if (useNewRender) {
    renderRow = (row, index) => (
      <SearchResultListItemNew
        key={row.id}
        productCode={row.productCode}
        onProductClick={onSearchItemClick}
        index={index}
        searchQuery={searchQuery}
      />
    );
  } else {
    if (userIsCustomer()) {
      renderRow = (row, index) => (
        <SearchResultProductCustomer
          key={row.id}
          productCode={row.productCode}
          onProductClick={onSearchItemClick}
          index={index}
          searchQuery={searchQuery}
        />
      );
    } else {
      renderRow = (row, index) => (
        <SearchResultProductNonCustomer key={row.id} resultRow={row} onProductClick={onSearchItemClick} index={index} />
      );
    }
  }

  const rows = results.map(renderRow);
  if (addQualtricsContainer) {
    rows.splice(
      3,
      0,
      <div key="qualtrics-container" id="search-qualtrics-container" className={"scha__search__qualtrics-container"} />,
    );
  }

  if (typeof recommendationPosition === "number" && recommendationComponent) {
    rows.splice(recommendationPosition, 0, recommendationComponent);
  }

  if (!isSalesCampaignPage) {
    if (assistantTools.length > 0) {
      rows.splice(3, 0, <LaunchAssistantsSection key={"LaunchAssistantsSection"} configs={assistantTools} />);
    }
    if (configuratorTools.length > 0) {
      rows.splice(3, 0, <LaunchConfiguratorsSection key={"LaunchConfiguratorsSection"} configs={configuratorTools} />);
    }
  }

  return (
    <ul key={searchQuery} className="scha__search__result-list" ref={listRef}>
      {rows}
    </ul>
  );
};

export default memo(SearchResultList);
